import { useEffect, useState } from "react";
import { List, X } from "@phosphor-icons/react";
import { openStrategySession } from "@/lib/calcom";
import Logo from "@/components/Logo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [menuOpen]);

  const linkClass =
    "text-[13px] font-mono text-zinc-400 hover:text-white transition-colors tracking-wide uppercase";

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const NAV_ITEMS = [
    { id: "pipeline", label: "The Engine" },
    { id: "architecture", label: "Architecture" },
    { id: "systems", label: "Systems" },
    { id: "paradigm", label: "Paradigm" },
    { id: "faq", label: "FAQ" },
  ];

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-2xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 h-16 flex items-center justify-between">
        {/* Logo */}
        <a href="#top" onClick={scrollTo("top")} data-testid="nav-logo">
          <Logo />
        </a>

        {/* Desktop Links */}
        <nav className="hidden md:flex items-center gap-8" data-testid="nav-links">
          {NAV_ITEMS.map((item) => (
            <a key={item.id} href={`#${item.id}`} onClick={scrollTo(item.id)}
              className={linkClass} data-testid={`nav-link-${item.id}`}>
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={openStrategySession}
            data-testid="nav-book-session-button"
            className="hidden sm:inline-flex font-mono text-[12px] uppercase tracking-[0.15em] bg-[#CCFF00] text-black font-bold px-4 py-2 hover:bg-white transition-colors"
          >
            Start Audit →
          </button>

          {/* Mobile menu trigger */}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            data-testid="nav-mobile-toggle"
            className="md:hidden h-10 w-10 flex items-center justify-center border border-white/15 text-white hover:border-white/40 transition-colors"
          >
            {menuOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      <div
        className={`md:hidden fixed inset-x-0 top-16 bottom-0 z-40 bg-[#050505]/98 backdrop-blur-2xl border-t border-white/10 transition-all duration-300 ${
          menuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        data-testid="nav-mobile-drawer"
      >
        <nav className="max-w-[1400px] mx-auto px-6 py-8 flex flex-col gap-1">
          {NAV_ITEMS.map((item, i) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={scrollTo(item.id)}
              data-testid={`nav-mobile-link-${item.id}`}
              className="py-5 border-b border-white/10 flex items-center justify-between group"
            >
              <div className="flex items-baseline gap-5">
                <span className="font-mono text-[11px] text-zinc-500 tabular-nums">
                  0{i + 1}
                </span>
                <span className="font-display font-bold uppercase tracking-tight text-white text-2xl group-hover:text-[#CCFF00] transition-colors">
                  {item.label}
                </span>
              </div>
              <span className="font-mono text-[11px] text-zinc-600">→</span>
            </a>
          ))}

          <button
            onClick={() => { setMenuOpen(false); openStrategySession(); }}
            data-testid="nav-mobile-book-session-button"
            className="mt-8 w-full bg-[#CCFF00] text-black font-mono font-bold py-5 flex items-center justify-center gap-3 uppercase tracking-[0.2em] text-[13px] hover:bg-white transition-colors"
          >
            Start Audit →
          </button>
        </nav>
      </div>
    </header>
  );
}
